import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import ReserveConsultationDialog, { ReserveConsultationFormValues } from '../components/ReserveConsultationDialog'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetConsultationReasonsQuery } from '@/core/services/consultation'
import { useSearchPatientsQuery } from '@/core/services/patient'
import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { useCanReserveConsultation } from '../model/useCanReserveConsultation'
import { useReserveConsultation } from '../model/useReserveConsultation'

export default function ReserveConsultationDialogContainer() {
  const { waitingRoomId } = useParams<{ waitingRoomId: string }>()

  const [searchPatientsArgs, setSearchPatientsArgs] = useState({ patientSource: PatientSourceEnum.MedconnectPatient })

  const { data: consultationReasons } = useGetConsultationReasonsQuery({})
  const { data: patients } = useSearchPatientsQuery(searchPatientsArgs, { selectFromResult: ({ data, ...rest }) => ({ ...rest, data: data?.items }) })
  const { data: waitingRoom, isLoading: isFetchingWaitingRoom } = useGetWaitingRoomQuery(waitingRoomId ?? skipToken)

  const canReserveConsultation = useCanReserveConsultation(waitingRoom)
  const { reserve, isReserving, paymentDialog } = useReserveConsultation(waitingRoom)

  const patientOptions = useMemo(
    () =>
      _.map(patients, (patient) => ({
        label: `${patient.userInfo.name} ${patient.userInfo.surname}`,
        value: patient._id,
      })),
    [patients]
  )

  const reasonOptions = useMemo(() => _.map(consultationReasons, 'name'), [consultationReasons])

  const specialityOptions = useMemo(
    () =>
      _.map(waitingRoom?.specialities, (speciality) => ({
        label: speciality.name,
        value: speciality.name,
      })),
    [waitingRoom]
  )

  const handleSearchPatient = useDebouncedCallback((value: string) => {
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, smartSearch: value || undefined }))
  }, 300)

  const handleReserveConsultation = (values: ReserveConsultationFormValues) => reserve(values)

  return (
    <>
      <ReserveConsultationDialog
        canReserve={canReserveConsultation}
        isLoading={isFetchingWaitingRoom}
        isReserving={isReserving}
        onSearchPatient={handleSearchPatient}
        onReserveConsultation={handleReserveConsultation}
        patientOptions={patientOptions}
        reasonOptions={reasonOptions}
        specialityOptions={specialityOptions}
        isPaid={waitingRoom?.price !== 0}
      />
      {paymentDialog}
    </>
  )
}
